import { Order } from 'store/cart/types';
import React, { useMemo } from 'react';
import { formatSummaryValues } from 'utils/formatSummaryValues';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocale } from 'hooks/use-locale.hook';
import { selectTransactionDisplay } from 'store/transactions/selectors';
import { PaymentDetailsItem } from 'components/payment/receipt/components/payment-details-item';
import { StyledPaymentDetails } from './styles';
import { AddOnDto } from 'store/add-ons/types';
import { getPaymentDetailsLabels } from 'utils/getPaymentDetailsLabels';
import { getPaymentDetailsValues } from 'utils/getPaymentDetailsValues';
import { selectDiscountByUuid, selectResource } from 'store/resource/selectors';
import { selectCurrentPaymentMethodId, selectPaymentMethods } from 'store/payment/selectors';
import { CREDITS } from 'shared/consts/payment-method-types';
import type {
  PaymentDetailsLabels,
  PaymentDetailsValues,
} from '@hqo/react-components-library/dist/payment/payment-details/payment-details.interface';
import { CreditsPaymentDetails } from './credits-payment-details';
import { usePriceQuery } from 'data/queries/getPriceQuery.hook';

interface CheckoutPaymentDetailsProps {
  cart: Order;
}

export const CheckoutPaymentDetails = ({ cart }: CheckoutPaymentDetailsProps): JSX.Element => {
  const intl = useIntl();
  const locale = useLocale();
  const transactionDisplay = useSelector(selectTransactionDisplay);
  const addOns = cart?.items[0]?.resource_booking?.add_ons;
  const currentDiscount = useSelector(selectDiscountByUuid(cart.items[0]?.discount_uuid));
  const discountAmount = currentDiscount?.amount ?? 0;
  const currentPaymentMethodId = useSelector(selectCurrentPaymentMethodId);
  const paymentMethods = useSelector(selectPaymentMethods);
  const resource = useSelector(selectResource);
  const priceQuery = usePriceQuery({
    resourceId: resource?.id,
    timezone: resource?.timezone,
  });
  const availableCredit = priceQuery?.data?.price?.available_credit;
  const creditTotal = priceQuery?.data?.price?.credit_total;
  const availableCreditAmount = availableCredit != null ? Math.ceil(availableCredit) : undefined;
  const creditTotalCeil = creditTotal != null ? Math.ceil(creditTotal) : undefined;
  const showCreditsInDetails =
    availableCreditAmount != null && creditTotalCeil != null && availableCreditAmount >= creditTotalCeil;

  const { currencyType, subtotal, discountApplied, total, taxSummary } = formatSummaryValues(
    cart?.total_summary || transactionDisplay?.total_summary,
  );

  const isCreditsApplicableForCart = useMemo(() => {
    const hasCreditPricing = cart?.items?.some(item => item.credit_price);
    const isCreditOnlyBooking = cart?.total_summary?.total === 0 && cart?.items?.some(item => item.credit_price);

    return hasCreditPricing || isCreditOnlyBooking;
  }, [cart?.items, cart?.total_summary?.total]);

  const isCreditsPayment = useMemo(() => {
    const selectedMethod = paymentMethods?.find(method => method.id.toString() === currentPaymentMethodId);
    return selectedMethod?.payment_method_type === CREDITS && isCreditsApplicableForCart;
  }, [paymentMethods, currentPaymentMethodId, isCreditsApplicableForCart]);

  const sortedAddOns = useMemo<AddOnDto[]>(() => addOns?.sort((a, b) => (a.quantity < b.quantity ? 1 : -1)), [addOns]);

  const creditPriceForDisplay = creditTotalCeil ?? cart?.items[0]?.credit_price;
  const showCreditsView = isCreditsPayment && showCreditsInDetails;
  const paymentDetailsValues = getPaymentDetailsValues(
    showCreditsView ? creditPriceForDisplay ?? 0 : cart?.items[0]?.price,
    total,
    subtotal,
    taxSummary,
    discountApplied,
    currencyType,
    locale,
    intl,
    discountAmount,
    showCreditsView,
  );

  if (showCreditsView) {
    return (
      <CreditsPaymentDetails
        itemName={cart?.items[0]?.display_info?.title}
        itemPrice={paymentDetailsValues.itemPrice}
      />
    );
  }

  return (
    <StyledPaymentDetails
      paymentItem={<PaymentDetailsItem addOns={sortedAddOns} currencyType={currencyType} />}
      paymentDetailsLabels={
        getPaymentDetailsLabels(cart?.items[0]?.display_info?.title, intl, showCreditsView) as PaymentDetailsLabels
      }
      paymentDetailsValues={paymentDetailsValues as PaymentDetailsValues}
    />
  );
};
